import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export type MenuTemplateKey = 'square_1080' | 'story_1920' | 'letter_print';

export interface Menu {
	id: string;
	user_id: string;
	name: string;
	description: string | null;
	template_key: MenuTemplateKey;
	sort_key: string;
	last_exported_at: string | null;
	last_export_url: string | null;
	last_export_template: string | null;
	cover_image_url: string | null;
	is_hidden: boolean;
	created_at: string;
	updated_at: string;
}

export const MENU_DESCRIPTION_MAX = 200;

export interface MenuItem {
	id: string;
	menu_id: string;
	user_item_id: string;
	position: number;
	created_at: string;
}

export const MENU_ITEM_CAP = 30;

const menusKey = (userId: string | undefined) => ['menus', userId] as const;
const menuKey = (menuId: string) => ['menu', menuId] as const;
const menuItemsKey = (menuId: string) => ['menu-items', menuId] as const;

export function useMyMenus() {
	const { user } = useAuth();
	return useQuery({
		queryKey: menusKey(user?.id),
		enabled: !!user?.id,
		queryFn: async (): Promise<Menu[]> => {
			const { data, error } = await supabase
				.from('menus' as any)
				.select('*')
				.order('updated_at', { ascending: false });
			if (error) throw error;
			return (data || []) as unknown as Menu[];
		}
	});
}

/**
 * Public menus for a given seller — every menu that has at least one item.
 * Returns up to 4 preview thumbnails per menu for the storefront badge collage.
 */
export interface ShippedMenu extends Menu {
	item_count: number;
	preview_images: string[];
	price_min: number | null;
	price_max: number | null;
}

export function useShippedMenusByUser(userId: string | undefined) {
	return useQuery({
		queryKey: ['shipped-menus', userId] as const,
		enabled: !!userId,
		queryFn: async (): Promise<ShippedMenu[]> => {
			const { data, error } = await supabase
				.from('menus' as any)
				.select(
					'*, menu_items(position, user_items(is_beta, asking_price_cents, stock_image_url, user_item_images(image_url, display_order)))'
				)
				.eq('user_id', userId!)
				.eq('is_hidden', false)
				.order('updated_at', { ascending: false });
			if (error) throw error;
			return ((data || []) as any[])
				.map((m) => {
					// Beta-visibility soft isolation: drop beta items from buyer surface.
					// See mem://features/beta-visibility-soft-isolation-v1
					const visible = (Array.isArray(m.menu_items) ? m.menu_items : []).filter(
						(mi: any) => mi?.user_items && mi.user_items.is_beta !== true
					);
					return { ...m, menu_items: visible };
				})
				.filter((m) => m.menu_items.length > 0)
				.map((m) => {
					const sorted = [...m.menu_items].sort(
						(a: any, b: any) => (a.position ?? 0) - (b.position ?? 0)
					);
					const previews: string[] = [];
					const prices: number[] = [];
					for (const mi of sorted) {
						const ui = mi.user_items;
						if (!ui) continue;
						const imgs = Array.isArray(ui.user_item_images) ? ui.user_item_images : [];
						const primary = [...imgs].sort(
							(a: any, b: any) => (a.display_order ?? 0) - (b.display_order ?? 0)
						)[0]?.image_url;
						const url = primary || ui.stock_image_url;
						if (url && previews.length < 4) previews.push(url);
						const cents =
							typeof ui.asking_price_cents === 'number'
								? ui.asking_price_cents
								: Number(ui.asking_price_cents);
						if (Number.isFinite(cents) && cents > 0) prices.push(cents / 100);
					}
					const { menu_items, ...rest } = m;
					return {
						...rest,
						item_count: menu_items.length,
						preview_images: previews,
						price_min: prices.length ? Math.min(...prices) : null,
						price_max: prices.length ? Math.max(...prices) : null
					} as ShippedMenu;
				});
		}
	});
}

export function useMenu(menuId: string | undefined) {
	return useQuery({
		queryKey: menuKey(menuId || ''),
		enabled: !!menuId,
		queryFn: async (): Promise<Menu | null> => {
			const { data, error } = await supabase
				.from('menus' as any)
				.select('*')
				.eq('id', menuId!)
				.maybeSingle();
			if (error) throw error;
			return (data as unknown as Menu) || null;
		}
	});
}

export function useMenuItems(menuId: string | undefined) {
	return useQuery({
		queryKey: menuItemsKey(menuId || ''),
		enabled: !!menuId,
		queryFn: async (): Promise<MenuItem[]> => {
			const { data, error } = await supabase
				.from('menu_items' as any)
				.select('*')
				.eq('menu_id', menuId!)
				.order('position', { ascending: true });
			if (error) throw error;
			return (data || []) as unknown as MenuItem[];
		}
	});
}

export function useCreateMenu() {
	const qc = useQueryClient();
	const { user } = useAuth();
	return useMutation({
		mutationFn: async (input: { name: string; description?: string | null; template_key?: MenuTemplateKey; item_ids?: string[] }) => {
			if (!user) throw new Error('Not signed in');
			const desc = typeof input.description === 'string'
				? input.description.trim().slice(0, MENU_DESCRIPTION_MAX) || null
				: null;
			const { data: menu, error: menuErr } = await supabase
				.from('menus' as any)
				.insert({
					user_id: user.id,
					name: input.name.trim().slice(0, 60),
					description: desc,
					template_key: input.template_key || 'square_1080'
				} as any)
				.select()
				.single();
			if (menuErr) throw menuErr;

			const ids = (input.item_ids || []).slice(0, MENU_ITEM_CAP);
			if (ids.length) {
				const rows = ids.map((user_item_id, i) => ({
					menu_id: (menu as any).id,
					user_item_id,
					position: i
				}));
				const { error: miErr } = await supabase.from('menu_items' as any).insert(rows as any);
				if (miErr) throw miErr;
			}
			return menu as unknown as Menu;
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: menusKey(user?.id) });
		}
	});
}

export function useUpdateMenu() {
	const qc = useQueryClient();
	const { user } = useAuth();
	return useMutation({
		mutationFn: async (input: { id: string; name?: string; description?: string | null; template_key?: MenuTemplateKey; cover_image_url?: string | null }) => {
			const patch: any = {};
			if (input.name !== undefined) patch.name = input.name.trim().slice(0, 60);
			if (input.description !== undefined) {
				patch.description = input.description === null
					? null
					: input.description.trim().slice(0, MENU_DESCRIPTION_MAX) || null;
			}
			if (input.template_key !== undefined) patch.template_key = input.template_key;
			if (input.cover_image_url !== undefined) patch.cover_image_url = input.cover_image_url;
			const { data, error } = await supabase
				.from('menus' as any)
				.update(patch)
				.eq('id', input.id)
				.select()
				.single();
			if (error) throw error;
			return data as unknown as Menu;
		},
		onSuccess: (_data, vars) => {
			qc.invalidateQueries({ queryKey: menusKey(user?.id) });
			qc.invalidateQueries({ queryKey: menuKey(vars.id) });
		}
	});
}

export function useDeleteMenu() {
	const qc = useQueryClient();
	const { user } = useAuth();
	return useMutation({
		mutationFn: async (id: string) => {
			const { error } = await supabase.from('menus' as any).delete().eq('id', id);
			if (error) throw error;
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: menusKey(user?.id) });
		}
	});
}

export function useToggleMenuHidden() {
	const qc = useQueryClient();
	const { user } = useAuth();
	return useMutation({
		mutationFn: async (input: { id: string; is_hidden: boolean }) => {
			const { data, error } = await supabase
				.from('menus' as any)
				.update({ is_hidden: input.is_hidden } as any)
				.eq('id', input.id)
				.select()
				.single();
			if (error) throw error;
			return data as unknown as Menu;
		},
		onSuccess: (_data, vars) => {
			qc.invalidateQueries({ queryKey: menusKey(user?.id) });
			qc.invalidateQueries({ queryKey: menuKey(vars.id) });
			qc.invalidateQueries({ queryKey: ['shipped-menus'] });
		}
	});
}

export function useAddItemsToMenu() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (input: { menu_id: string; user_item_ids: string[] }) => {
			// Compute next available positions
			const { data: existing } = await supabase
				.from('menu_items' as any)
				.select('user_item_id, position')
				.eq('menu_id', input.menu_id)
				.order('position', { ascending: false });
			const existingIds = new Set((existing || []).map((r: any) => r.user_item_id));
			const nextPos = ((existing?.[0] as any)?.position ?? -1) + 1;
			const toAdd = input.user_item_ids.filter((id) => !existingIds.has(id));
			const remaining = MENU_ITEM_CAP - (existing?.length || 0);
			if (remaining <= 0) {
				throw new Error(`Menu is full (${MENU_ITEM_CAP} items max).`);
			}
			const limited = toAdd.slice(0, remaining);
			if (limited.length === 0) return { added: 0, skipped: input.user_item_ids.length };

			const rows = limited.map((user_item_id, i) => ({
				menu_id: input.menu_id,
				user_item_id,
				position: nextPos + i
			}));
			const { error } = await supabase.from('menu_items' as any).insert(rows as any);
			if (error) throw error;
			return { added: limited.length, skipped: input.user_item_ids.length - limited.length };
		},
		onSuccess: (_data, vars) => {
			qc.invalidateQueries({ queryKey: menuItemsKey(vars.menu_id) });
			qc.invalidateQueries({ queryKey: menuKey(vars.menu_id) });
		}
	});
}

export function useRemoveMenuItem() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (input: { menu_id: string; menu_item_id: string }) => {
			const { error } = await supabase
				.from('menu_items' as any)
				.delete()
				.eq('id', input.menu_item_id);
			if (error) throw error;
		},
		onSuccess: (_d, vars) => {
			qc.invalidateQueries({ queryKey: menuItemsKey(vars.menu_id) });
		}
	});
}

export function useReorderMenuItems() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (input: { menu_id: string; ordered_menu_item_ids: string[] }) => {
			// Sequentially update positions to satisfy unique constraints
			for (let i = 0; i < input.ordered_menu_item_ids.length; i++) {
				const id = input.ordered_menu_item_ids[i];
				const { error } = await supabase
					.from('menu_items' as any)
					.update({ position: i + 1000 } as any) // shift first to avoid unique clash
					.eq('id', id);
				if (error) throw error;
			}
			for (let i = 0; i < input.ordered_menu_item_ids.length; i++) {
				const id = input.ordered_menu_item_ids[i];
				const { error } = await supabase
					.from('menu_items' as any)
					.update({ position: i } as any)
					.eq('id', id);
				if (error) throw error;
			}
		},
		onSuccess: (_d, vars) => {
			qc.invalidateQueries({ queryKey: menuItemsKey(vars.menu_id) });
		}
	});
}

export function useRenderMenu() {
	const qc = useQueryClient();
	const { user } = useAuth();
	return useMutation({
		mutationFn: async (input: { menu_id: string; template_key: MenuTemplateKey }) => {
			const { data, error } = await supabase.functions.invoke('render-menu', { body: input });
			if (error) throw error;
			return data as { url: string; template_key: string; expires_at: string; path: string };
		},
		onSuccess: (_d, vars) => {
			qc.invalidateQueries({ queryKey: menuKey(vars.menu_id) });
			qc.invalidateQueries({ queryKey: menusKey(user?.id) });
		}
	});
}

/**
 * Public menu destination — full menu + its items in curator order
 * (ordered by menu_items.position). Used by /u/:username/menu/:id.
 * Joins the same user_items fields a StorefrontItemCard needs and
 * enforces buyer-side beta isolation.
 */
export interface PublicMenuDetail {
	menu: Menu & { username: string | null; avatar_url: string | null };
	items: any[];
	price_min: number | null;
	price_max: number | null;
}

export function useMenuById(menuId: string | undefined) {
	return useQuery({
		queryKey: ['public-menu', menuId] as const,
		enabled: !!menuId,
		queryFn: async (): Promise<PublicMenuDetail | null> => {
			const { data: menu, error } = await supabase
				.from('menus' as any)
				.select('*')
				.eq('id', menuId!)
				.maybeSingle();
			if (error) throw error;
			if (!menu) return null;
			const m = menu as any;
			const { data: prof } = await supabase
				.from('profiles')
				.select('username, avatar_url')
				.eq('user_id', m.user_id)
				.maybeSingle();
			const { data: rows } = await supabase
				.from('menu_items' as any)
				.select(
					'position, user_items(id, short_id, user_id, title, asking_price_cents, category, condition, set_code, card_number, grading_company, slab_grade, product_type, era, game, visibility, is_negotiable, has_proof_pack, quantity, previous_price_cents, price_dropped_at, stock_image_url, is_beta, user_item_images(image_url, display_order))'
				)
				.eq('menu_id', menuId!)
				.order('position', { ascending: true });
			const items = ((rows || []) as any[])
				.map((r) => r.user_items)
				.filter((ui) => ui && ui.is_beta !== true)
				.map((ui) => ({
					...ui,
					images: (Array.isArray(ui.user_item_images) ? ui.user_item_images : [])
						.slice()
						.sort((a: any, b: any) => (a.display_order ?? 0) - (b.display_order ?? 0))
						.map((img: any) => ({ image_url: img.image_url }))
				}));
			const prices = items
				.map((i: any) => Number(i.asking_price_cents))
				.filter((n: number) => Number.isFinite(n) && n > 0)
				.map((n: number) => n / 100);
			return {
				menu: { ...(m as Menu), username: prof?.username ?? null, avatar_url: prof?.avatar_url ?? null },
				items,
				price_min: prices.length ? Math.min(...prices) : null,
				price_max: prices.length ? Math.max(...prices) : null
			};
		}
	});
}

/**
 * Clone a menu (and its ordered items) into a new draft. Cover, description,
 * template carry over; export URLs and hidden flag do not.
 */
export function useCloneMenu() {
	const qc = useQueryClient();
	const { user } = useAuth();
	return useMutation({
		mutationFn: async (menuId: string): Promise<Menu> => {
			if (!user) throw new Error('Not signed in');
			const { data: src, error: srcErr } = await supabase
				.from('menus' as any)
				.select('*')
				.eq('id', menuId)
				.maybeSingle();
			if (srcErr || !src) throw srcErr || new Error('Menu not found');
			const m = src as any;
			const cloneName = `${m.name} (copy)`.slice(0, 60);
			const { data: created, error: createErr } = await supabase
				.from('menus' as any)
				.insert({
					user_id: user.id,
					name: cloneName,
					description: m.description ?? null,
					template_key: m.template_key ?? 'square_1080',
					cover_image_url: m.cover_image_url ?? null,
				} as any)
				.select()
				.single();
			if (createErr) throw createErr;

			const { data: items } = await supabase
				.from('menu_items' as any)
				.select('user_item_id, position')
				.eq('menu_id', menuId)
				.order('position', { ascending: true });
			const rows = (items || []).map((r: any, i: number) => ({
				menu_id: (created as any).id,
				user_item_id: r.user_item_id,
				position: i,
			}));
			if (rows.length) {
				const { error: miErr } = await supabase
					.from('menu_items' as any)
					.insert(rows as any);
				if (miErr) throw miErr;
			}
			return created as unknown as Menu;
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: menusKey(user?.id) });
		},
	});
}
